import React, { useState, useEffect } from 'react';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Typography, Grid, Button, TextField, Avatar,
  Divider, List, ListItem, ListItemText, Chip, Card, CardContent,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const MemberCenter = () => {
  const { t } = useTranslation('member');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, logout, updateProfile, loading: authLoading } = useAuth();

  // 從 URL 參數讀取初始 tab
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const [tabValue, setTabValue] = useState(initialTab);
  const [viewedProducts, setViewedProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);

  // 未登入時導向登入頁
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/member' } } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // 載入會員資料
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  // 載入訂單
  useEffect(() => {
    if (isAuthenticated && tabValue === 1) {
      fetchOrders();
    }
  }, [isAuthenticated, tabValue]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await api.get('/orders/my-orders');
      setOrders(response.data);
    } catch (error) {
      // 開發環境使用模擬資料
      setOrders([
        {
          id: 'ORD-2024120301',
          createdAt: '2024-12-03T10:30:00Z',
          status: 'delivered',
          statusText: t('orders.status_delivered'),
          total: 599,
          shippingFee: 0,
          paymentMethod: t('orders.mock_payment_cvs_cod'),
          shippingMethod: t('orders.mock_shipping_711'),
          trackingNumber: 'CVS123456789',
          items: [
            { id: 1, name: t('orders.mock_product_1'), quantity: 2, price: 29 },
            { id: 2, name: t('orders.mock_product_2'), quantity: 3, price: 5 }
          ]
        },
        {
          id: 'ORD-2024120102',
          createdAt: '2024-12-01T15:20:00Z',
          status: 'shipping',
          statusText: t('orders.status_shipping'),
          total: 459,
          shippingFee: 60,
          paymentMethod: 'LINE Pay',
          shippingMethod: t('orders.mock_shipping_family'),
          trackingNumber: 'CVS987654321',
          items: [
            { id: 3, name: t('orders.mock_product_3'), quantity: 1, price: 99 },
            { id: 4, name: t('orders.mock_product_4'), quantity: 2, price: 150 }
          ]
        },
        {
          id: 'ORD-2024112801',
          createdAt: '2024-11-28T09:00:00Z',
          status: 'pending',
          statusText: t('orders.status_pending'),
          total: 299,
          shippingFee: 60,
          paymentMethod: t('orders.mock_payment_cvs_code'),
          shippingMethod: t('orders.mock_shipping_711'),
          trackingNumber: null,
          items: [
            { id: 5, name: t('orders.mock_product_5'), quantity: 1, price: 239 }
          ]
        }
      ]);
    }
    setLoadingOrders(false);
  };

  // 儲存個人資料
  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const result = await updateProfile(profileForm);

    if (result.success) {
      setMessage({ type: 'success', text: t('profile.update_success') });
      setEditMode(false);
    } else {
      setMessage({ type: 'error', text: result.error || t('profile.update_failed') });
    }

    setSaving(false);
  };

  // 登出
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 取得訂單狀態顏色
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'info',
      processing: 'info',
      shipping: 'primary',
      delivered: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* 左側邊欄 - 會員資訊 */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={user?.avatar}
              sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}
            >
              {user?.name?.charAt(0) || <PersonIcon />}
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              {user?.name || t('center.member')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {user?.phone || user?.email}
            </Typography>
            <Chip
              label={user?.provider === 'google' ? t('center.google_account') : user?.provider === 'line' ? t('center.line_account') : t('center.phone_member')}
              size="small"
              color="primary"
              variant="outlined"
            />

            <Divider sx={{ my: 2 }} />

            <List dense>
              <ListItem button selected={tabValue === 0} onClick={() => setTabValue(0)}>
                <PersonIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary={t('center.profile')} />
              </ListItem>
              <ListItem button selected={tabValue === 1} onClick={() => setTabValue(1)}>
                <ShoppingBagIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary={t('center.orders')} />
              </ListItem>
              <ListItem button selected={tabValue === 2} onClick={() => setTabValue(2)}>
                <FavoriteIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary={t('center.favorites')} />
              </ListItem>
              <ListItem button selected={tabValue === 3} onClick={() => setTabValue(3)}>
                <LocalShippingIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary={t('center.addresses')} />
              </ListItem>
              <ListItem button selected={tabValue === 4} onClick={() => setTabValue(4)}>
                <HistoryIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary={t('center.history')} />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              {t('center.logout')}
            </Button>
          </Paper>
        </Grid>

        {/* 右側內容區 */}
        <Grid item xs={12} md={9}>
          {/* 個人資料 */}
          {tabValue === 0 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  {t('center.profile')}
                </Typography>
                {!editMode ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    {t('profile.edit')}
                  </Button>
                ) : (
                  <Box>
                    <Button onClick={() => setEditMode(false)} sx={{ mr: 1 }}>
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={20} /> : t('profile.save')}
                    </Button>
                  </Box>
                )}
              </Box>

              {message.text && (
                <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
                  {message.text}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profile.name')}
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('profile.phone')}
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    disabled={!editMode || user?.provider === 'phone'}
                    InputProps={{
                      startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText={user?.provider === 'phone' ? t('profile.phone_readonly') : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.email')}
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    disabled={!editMode || user?.provider === 'google'}
                    InputProps={{
                      startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText={user?.provider === 'google' ? t('profile.google_readonly') : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('profile.default_address')}
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    disabled={!editMode}
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: <HomeIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('profile.account_security')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.login_method')}: {user?.provider === 'google' ? t('center.google_account') : user?.provider === 'line' ? t('center.line_account') : t('profile.phone')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.registered_at')}: {user?.createdAt ? formatDate(user.createdAt) : t('profile.unknown')}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* 我的訂單 */}
          {tabValue === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('center.orders')}
              </Typography>

              {loadingOrders ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <ShoppingBagIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">{t('orders.empty')}</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/products')}>
                    {t('orders.start_shopping')}
                  </Button>
                </Box>
              ) : (
                <Box>
                  {orders.map((order) => (
                    <Card key={order.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('orders.order_no')}: {order.id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(order.createdAt)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip
                              label={order.statusText}
                              color={getStatusColor(order.status)}
                              size="small"
                            />
                            <Typography variant="h6" color="error" sx={{ mt: 1 }}>
                              ${order.total}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('orders.payment_method')}: {order.paymentMethod}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('orders.shipping_method')}: {order.shippingMethod}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {order.trackingNumber && (
                              <Typography variant="body2" color="text.secondary">
                                {t('orders.tracking_no')}: {order.trackingNumber}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {t('orders.items_count', { count: order.items.reduce((sum, item) => sum + item.quantity, 0) })}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => setSelectedOrder(order)}
                          >
                            {t('orders.view_detail')}
                          </Button>
                          {order.status === 'shipping' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<LocalShippingIcon />}
                            >
                              {t('orders.track_shipping')}
                            </Button>
                          )}
                          {order.status === 'pending' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                            >
                              {t('orders.pay_now')}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          {/* 收藏清單 */}
          {tabValue === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('center.favorites')}
              </Typography>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <FavoriteIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">{t('favorites.empty')}</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/products')}>
                  {t('favorites.explore')}
                </Button>
              </Box>
            </Paper>
          )}

          {/* 常用地址 */}
          {tabValue === 3 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  {t('center.addresses')}
                </Typography>
                <Button variant="outlined" startIcon={<EditIcon />}>
                  {t('addresses.add')}
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                {t('addresses.hint')}
              </Alert>

              {profileForm.address ? (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Chip label={t('addresses.default')} size="small" color="primary" sx={{ mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {profileForm.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {profileForm.phone}
                        </Typography>
                        <Typography variant="body2">
                          {profileForm.address}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => setTabValue(0)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HomeIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">{t('addresses.empty')}</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => { setTabValue(0); setEditMode(true); }}>
                    {t('addresses.add')}
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {/* 瀏覽紀錄 */}
          {tabValue === 4 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('center.history')}
              </Typography>

              <RecentlyViewedProducts navigate={navigate} />
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* 訂單詳情 Dialog */}
      <Dialog
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('orders.order_detail')}
                </Typography>
                <Chip label={selectedOrder.statusText} color={getStatusColor(selectedOrder.status)} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.order_no')}: {selectedOrder.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('orders.order_time')}: {formatDate(selectedOrder.createdAt)}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('orders.items_detail')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('orders.product_name')}</TableCell>
                      <TableCell align="center">{t('orders.quantity')}</TableCell>
                      <TableCell align="right">{t('orders.unit_price')}</TableCell>
                      <TableCell align="right">{t('orders.subtotal')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.price * item.quantity}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right">{t('orders.shipping_fee')}</TableCell>
                      <TableCell align="right">
                        {selectedOrder.shippingFee > 0 ? `$${selectedOrder.shippingFee}` : t('orders.free_shipping')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <Typography fontWeight="bold">{t('orders.order_total')}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="error">${selectedOrder.total}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold">{t('orders.payment_info')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.paymentMethod}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold">{t('orders.shipping_info')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.shippingMethod}
                  </Typography>
                  {selectedOrder.trackingNumber && (
                    <Typography variant="body2" color="primary">
                      {t('orders.tracking_no')}: {selectedOrder.trackingNumber}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedOrder(null)}>{t('orders.close')}</Button>
              {selectedOrder.status === 'shipping' && (
                <Button variant="contained" startIcon={<LocalShippingIcon />}>
                  {t('orders.track_shipping')}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

// 瀏覽紀錄組件
const RecentlyViewedProducts = ({ navigate }) => {
  const { t } = useTranslation('member');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    // 從 localStorage 讀取瀏覽紀錄
    const viewedHistory = JSON.parse(localStorage.getItem('viewedHistory') || '[]');
    setProducts(viewedHistory);
  }, []);

  const clearHistory = () => {
    localStorage.removeItem('viewedHistory');
    setProducts([]);
  };

  if (products.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary">{t('history.empty')}</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/products')}
        >
          {t('history.browse')}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button size="small" color="error" onClick={clearHistory}>
          {t('history.clear')}
        </Button>
      </Box>
      <Grid container spacing={2}>
        {products.map((product) => (
          <Grid item xs={6} sm={4} md={3} key={product.id}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <Box
                component="img"
                src={normalizeImageUrl(product.image, product.name)}
                onError={(e) => onImageErrorSetFallback(e, product.name)}
                alt={product.name}
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover'
                }}
              />
              <CardContent sx={{ p: 1.5 }}>
                <Typography
                  variant="body2"
                  noWrap
                  title={product.name}
                >
                  {product.name}
                </Typography>
                <Typography variant="subtitle2" color="error" fontWeight="bold">
                  ${product.price}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(product.viewedAt).toLocaleDateString('zh-TW')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default MemberCenter;
